import { inject, Injectable } from "@angular/core";

import { MarkdownService } from "./markdown.service";

@Injectable({
  providedIn: "root"
})
export class TextExportService {
  private readonly _markdownService = inject(MarkdownService);

  /**
   * Export document as plain text
   */
  exportToText(markdown: string): string {
    if (!markdown) return "";

    return (
      markdown
        // Remove markdown headers
        .replace(/^#{1,6}\s+/gm, "")
        // Remove markdown links, keep text
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        // Remove markdown emphasis
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/\*([^*]+)\*/g, "$1")
        .replace(/_([^_]+)_/g, "$1")
        // Remove markdown code blocks
        .replace(/```[\s\S]*?```/g, "")
        .replace(/`([^`]+)`/g, "$1")
        // Remove markdown lists
        .replace(/^[*+-]\s+/gm, "")
        .replace(/^\d+\.\s+/gm, "")
        // Clean up extra whitespace
        .replace(/\n{3,}/g, "\n\n")
        .trim()
    );
  }

  /**
   * Copy text with formatting to clipboard
   */
  async copyTextWithFormatting(markdown: string): Promise<void> {
    try {
      // Convert markdown to HTML for rich text formatting
      const html = this._markdownService.markdownToHtml(markdown);
      const plainText = this.exportToText(markdown);

      // Try modern clipboard API with rich content
      if (navigator?.clipboard?.write) {
        const htmlBlob = new Blob([html], { type: "text/html" });
        const textBlob = new Blob([plainText], { type: "text/plain" });

        const clipboardItem = new ClipboardItem({
          "text/html": htmlBlob,
          "text/plain": textBlob
        });

        await navigator.clipboard.write([clipboardItem]);
        return;
      }

      // Fallback: Try writeText with HTML (some browsers support HTML in writeText)
      if (navigator?.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(html);
          return;
        } catch {
          // Fall back to plain text
          await navigator.clipboard.writeText(plainText);
          return;
        }
      }
    } catch (err) {
      console.warn("Rich text clipboard failed, falling back to plain text", err);
    }

    // Ultimate fallback to plain text
    await this._copyToClipboard(this.exportToText(markdown));
  }

  /**
   * Copy plain text to clipboard
   */
  async copyToClipboard(text: string): Promise<void> {
    return this._copyToClipboard(text);
  }

  /**
   * Private method to handle clipboard operations
   */
  private async _copyToClipboard(text: string): Promise<void> {
    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch (err) {
        console.warn("Clipboard API failed, falling back to document.execCommand", err);
      }
    }

    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "-9999px";
    textArea.style.opacity = "0";

    try {
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      if (!document.execCommand("copy")) {
        throw new Error("execCommand copy failed");
      }
    } finally {
      document.body.removeChild(textArea);
    }
  }
}
